import { useEffect, useMemo, useState } from "react";
import {
  AreaChart,
  Area,
  BarChart,
  Bar,
  XAxis,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  ScatterChart,
  Scatter,
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  Radar,
  ComposedChart,
  Line,
  FunnelChart,
  Funnel,
  LabelList,
  RadialBarChart,
  RadialBar,
  Legend,
  CartesianGrid,
  YAxis,
} from "recharts";
import { motion, AnimatePresence } from "framer-motion";
import {
  Building2,
  Search,
  Activity,
  Users,
  Zap,
  ChevronDown,
  Loader2,
  Sparkles,
  X,
} from "lucide-react";
import { useStore } from "../store/useStore";
import { ChartCard } from "../components/ui/ChartCard";
import { formatCurrency, cn } from "../utils/helpers";

interface DashboardAnalyticsResponse {
  kpis: {
    totalRevenue: number;
    activePipeline: number;
    avgYield: number;
    totalClients: number;
    r2: number;
  };
  revenueTrend: Array<{
    period: string;
    revenue: number;
    cost: number;
  }>;
  conversionFunnel: Array<{
    stage: string;
    value: number;
  }>;
  assetAllocation: Array<{
    type: string;
    value: number;
  }>;
  valuationMatrix: Array<{
    sqft: number;
    price: number;
    psf: number;
  }>;
  kpiTargets: Array<{
    category: string;
    value: number;
  }>;
  momentum: Array<{
    period: string;
    volume: number;
    deals: number;
  }>;
  portfolioScore: Array<{
    metric: string;
    score: number;
  }>;
  agentPerformance: Array<{
    agent: string;
    score: number;
  }>;
}

interface DashboardPropertiesResponse {
  data: Array<{
    id: string;
    communityName: string | null;
    propertyName: string | null;
    developer: string | null;
    totalUnits: number | null;
    unitsSold: number | null;
    unitsAvailable: number | null;
    soldPercentage: number | null;
    priceRange:
      | {
          min: number | null;
          max: number | null;
        }
      | null;
    units:
      | Array<{
          buildingName: string | null;
          unitNumber: string | null;
          unitType: string | null;
          area: number | null;
          price: number | null;
          status?: string | null;
          floor?: number | string | null;
        }>
      | null;
  }>;
}

interface DashboardKpis {
  totalRevenue: number;
  activePipeline: number;
  avgYield: number;
  totalClients: number;
  r2: number;
}

const DASHBOARD_ANALYTICS_URL =
  "https://ddre.kendal.ai/api-analytics/v1/analytics?startDate=2025-01-31&endDate=2025-12-31";
const DASHBOARD_PROPERTIES_URL =
  "https://ddre.kendal.ai/api-analytics/v1/properties?page=1&limit=20&sortBy=exchangePrice&order=desc";

const formatPound = (value: number, digits = 0) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    minimumFractionDigits: digits,
    maximumFractionDigits: digits,
  }).format(value);

const mapTooltipLabel = (dataKey: string, fallbackName: string) => {
  const labels: Record<string, string> = {
    Sales: "Sales",
    Cost: "Cost",
    Volume: "Sales Volume",
    "Deal Count": "Deal Count",
    Score: "Score",
    v: "Sales Volume",
    x: "Size (SqFt)",
    y: "Price",
    z: "£ / SqFt",
  };
  return labels[dataKey] || fallbackName || dataKey || "Value";
};

const formatTooltipValue = (
  dataKey: string,
  value: unknown,
): string | number => {
  if (typeof value !== "number") return String(value ?? "");
  const currencyKeys = new Set(["Sales", "Cost", "Volume", "v", "y"]);
  if (currencyKeys.has(dataKey)) return formatCurrency(value);
  if (dataKey === "z") return formatPound(value, 2);
  if (dataKey === "x") return value.toLocaleString();
  if (Number.isInteger(value)) return value.toLocaleString();
  return value.toFixed(2);
};

type DashboardProject = DashboardPropertiesResponse["data"][number];

const CustomTooltip = ({ active, payload, label }: any) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-slate-900/90 backdrop-blur-xl border border-slate-700/50 p-3 rounded-xl shadow-2xl z-50 min-w-[150px]">
        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-wider mb-2 border-b border-slate-700/50 pb-1">
          {label || "Data Point"}
        </p>
        {payload.map((entry: any, index: number) => (
          <div
            key={index}
            className="flex items-center justify-between gap-4 text-xs mb-1 last:mb-0"
          >
            <div className="flex items-center gap-2">
              <div
                className="w-2 h-2 rounded-full shadow-[0_0_8px_currentColor]"
                style={{ color: entry.color, backgroundColor: entry.color }}
              />
              <span className="text-slate-300 font-medium">
                {mapTooltipLabel(
                  String(entry.dataKey || ""),
                  String(entry.payload?.name || entry.name || ""),
                )}
              </span>
            </div>
            <span className="text-white font-mono font-bold">
              {formatTooltipValue(String(entry.dataKey || ""), entry.value)}
            </span>
          </div>
        ))}
      </div>
    );
  }
  return null;
};

const COLORS = [
  "#6366f1",
  "#10b981",
  "#f59e0b",
  "#ec4899",
  "#3b82f6",
  "#8b5cf6",
];

const truncateLabel = (value: string, max = 14) => {
  if (value.length <= max) return value;
  return `${value.slice(0, max - 1)}...`;
};

const collapseSeries = <T extends Record<string, any>>(
  data: T[],
  valueKey: keyof T,
  labelKey: keyof T,
  maxItems: number,
): T[] => {
  if (data.length <= maxItems) return data;

  const sorted = [...data].sort(
    (a, b) => Number(b[valueKey] ?? 0) - Number(a[valueKey] ?? 0),
  );

  const keepCount = Math.max(1, maxItems - 1);
  const kept = sorted.slice(0, keepCount);
  const rest = sorted.slice(keepCount);
  const othersValue = rest.reduce(
    (sum, item) => sum + Number(item[valueKey] ?? 0),
    0,
  );

  if (othersValue <= 0) return kept;

  return [
    ...kept,
    {
      ...sorted[0],
      [labelKey]: "Others",
      [valueKey]: othersValue,
    },
  ];
};

export default function Dashboard() {
  const properties = useStore((state) => state.properties);
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [isSearchPending, setIsSearchPending] = useState(false);
  const [enlargedChart, setEnlargedChart] = useState<string | null>(null);
  const [apiAnalytics, setApiAnalytics] =
    useState<DashboardAnalyticsResponse | null>(null);
  const [isAnalyticsLoading, setIsAnalyticsLoading] = useState(true);
  const [hasAnalyticsFallback, setHasAnalyticsFallback] = useState(false);
  const [apiProjects, setApiProjects] = useState<DashboardProject[]>([]);
  const [isProjectsLoading, setIsProjectsLoading] = useState(true);
  const [hasProjectFallback, setHasProjectFallback] = useState(false);
  const [expandedProjectId, setExpandedProjectId] = useState<string | null>(
    null,
  );

  useEffect(() => {
    let isCancelled = false;

    const fetchDashboardData = async () => {
      setIsAnalyticsLoading(true);
      setIsProjectsLoading(true);
      try {
        const [analyticsResponse, propertiesResponse] = await Promise.all([
          fetch(DASHBOARD_ANALYTICS_URL, {
            headers: { "Content-Type": "application/json" },
          }),
          fetch(DASHBOARD_PROPERTIES_URL, {
            headers: { "Content-Type": "application/json" },
          }),
        ]);

        if (!analyticsResponse.ok || !propertiesResponse.ok) {
          throw new Error(
            `Analytics request failed: ${analyticsResponse.status}/${propertiesResponse.status}`,
          );
        }

        const analyticsJson =
          (await analyticsResponse.json()) as DashboardAnalyticsResponse;
        const propertiesJson =
          (await propertiesResponse.json()) as DashboardPropertiesResponse;

        if (isCancelled) return;

        setApiAnalytics(analyticsJson);
        setHasAnalyticsFallback(false);
        setApiProjects(propertiesJson.data || []);
        setHasProjectFallback(false);
      } catch (error) {
        if (isCancelled) return;
        console.error(
          "Dashboard API unavailable. Falling back to mock data.",
          error,
        );
        setApiAnalytics(null);
        setHasAnalyticsFallback(true);
        setApiProjects([]);
        setHasProjectFallback(true);
      } finally {
        if (!isCancelled) {
          setIsAnalyticsLoading(false);
          setIsProjectsLoading(false);
        }
      }
    };

    fetchDashboardData();

    return () => {
      isCancelled = true;
    };
  }, []);

  const fallbackProjects = useMemo<DashboardProject[]>(
    () =>
      properties.map((property) => ({
        id: property.id,
        communityName: property.community || property.city || null,
        propertyName: property.address || null,
        developer: "Unknown",
        totalUnits: null,
        unitsSold: null,
        unitsAvailable: null,
        soldPercentage: null,
        priceRange: {
          min: property.askingPrice || property.exchangePrice || null,
          max: property.exchangePrice || property.askingPrice || null,
        },
        units: [
          {
            buildingName: property.address || null,
            unitNumber: null,
            unitType: property.propertyType || null,
            area: property.sqft || null,
            price: property.exchangePrice || null,
            status: property.status || null,
            floor: null,
          },
        ],
      })),
    [properties],
  );

  const projectRows = useMemo(
    () => {
      if (isProjectsLoading) return [];
      if (apiProjects.length > 0) return apiProjects;
      if (hasProjectFallback) return fallbackProjects;
      return [];
    },
    [apiProjects, fallbackProjects, hasProjectFallback, isProjectsLoading],
  );

  useEffect(() => {
    const nextSearch = searchInput.trim().toLowerCase();
    if (nextSearch === debouncedSearch) {
      setIsSearchPending(false);
      return;
    }

    setIsSearchPending(true);
    const timer = setTimeout(() => {
      setDebouncedSearch(nextSearch);
      setIsSearchPending(false);
    }, 220);

    return () => clearTimeout(timer);
  }, [debouncedSearch, searchInput]);

  const normalizeSearchText = (value: unknown) =>
    String(value ?? "")
      .toLowerCase()
      .replace(/\s+/g, " ")
      .trim();

  const indexedProjects = useMemo(
    () =>
      projectRows.map((project) => {
        const projectParts = [
          project.id,
          project.communityName,
          project.propertyName,
          project.developer,
          project.totalUnits,
          project.unitsSold,
          project.unitsAvailable,
          project.soldPercentage,
          project.priceRange?.min,
          project.priceRange?.max,
        ];

        const unitParts = (project.units || []).flatMap((unit) => [
          unit.buildingName,
          unit.unitNumber,
          unit.unitType,
          unit.area,
          unit.price,
          unit.status,
          unit.floor,
        ]);

        const searchBlob = normalizeSearchText([...projectParts, ...unitParts].join(" "));
        return { project, searchBlob };
      }),
    [projectRows],
  );

  const filteredProjects = useMemo(() => {
    if (!debouncedSearch) return indexedProjects.map((entry) => entry.project);

    const searchTerms = debouncedSearch.split(/\s+/).filter(Boolean);
    if (searchTerms.length === 0) return indexedProjects.map((entry) => entry.project);

    return indexedProjects
      .filter((entry) => searchTerms.every((term) => entry.searchBlob.includes(term)))
      .map((entry) => entry.project);
  }, [debouncedSearch, indexedProjects]);

  const normalizedSearchInput = searchInput.trim().toLowerCase();
  const isSearchSyncing =
    normalizedSearchInput.length > 0 && normalizedSearchInput !== debouncedSearch;

  const isMissingValue = (value: unknown) => {
    if (value === null || value === undefined) return true;
    if (typeof value !== "string") return false;
    const normalized = value.trim().toLowerCase();
    return !normalized || normalized === "unknown" || normalized === "undefined";
  };

  const displayText = (value: unknown) => {
    if (isMissingValue(value)) return "N/A";
    return String(value);
  };

  const toFiniteNumber = (value: unknown): number | null => {
    if (value === null || value === undefined) return null;
    const numeric = Number(value);
    return Number.isFinite(numeric) ? numeric : null;
  };

  const displayCount = (value: unknown) => {
    const numeric = toFiniteNumber(value);
    if (numeric === null) return "N/A";
    return numeric.toLocaleString();
  };

  const displayArea = (value: unknown) => {
    const numeric = toFiniteNumber(value);
    if (numeric === null) return "N/A";
    return numeric.toLocaleString(undefined, {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
  };

  const displayCurrency = (value: unknown) => {
    const numeric = toFiniteNumber(value);
    if (numeric === null) return "N/A";
    return formatCurrency(numeric);
  };

  const displayPriceRange = (
    range: DashboardProject["priceRange"] | null | undefined,
  ) => {
    const min = toFiniteNumber(range?.min);
    const max = toFiniteNumber(range?.max);
    if (min === null && max === null) return "N/A";
    if (min === null && max !== null) return `Up to ${formatCurrency(max)}`;
    if (min !== null && max === null) return `From ${formatCurrency(min)}`;
    return `${formatCurrency(min as number)} - ${formatCurrency(max as number)}`;
  };

  const displayCommunityAndProperty = (project: DashboardProject) => {
    const community = displayText(project.communityName);
    const propertyName = displayText(project.propertyName);
    if (community === "N/A" && propertyName === "N/A") return "N/A";
    if (community === "N/A") return propertyName;
    if (propertyName === "N/A") return community;
    return `${community} / ${propertyName}`;
  };

  const fallbackKpis = useMemo<DashboardKpis>(() => {
    const totalRevenue = properties.reduce(
      (sum, property) => sum + (property.exchangePrice || 0),
      0,
    );
    const avgYield =
      properties.length > 0
        ? properties.reduce(
            (sum, property) => sum + (property.pricePSF || 0),
            0,
          ) / properties.length
        : 0;
    const activeCountFromStatus = properties.filter((property) =>
      ["active", "available", "listing"].some((statusToken) =>
        (property.status || "").toLowerCase().includes(statusToken),
      ),
    ).length;

    return {
      totalRevenue,
      activePipeline: activeCountFromStatus || properties.length,
      avgYield,
      totalClients: properties.length,
      r2: 0.88,
    };
  }, [properties]);

  const emptyKpis = useMemo<DashboardKpis>(
    () => ({
      totalRevenue: 0,
      activePipeline: 0,
      avgYield: 0,
      totalClients: 0,
      r2: 0,
    }),
    [],
  );

  const dashboardKpis =
    apiAnalytics?.kpis || (hasAnalyticsFallback ? fallbackKpis : emptyKpis);

  // --- DATA MEMOS ---
  const fallbackRevenueTrend = useMemo(
    () =>
      properties.slice(0, 10).map((p, i) => ({
        period: `W${i + 1}`,
        Sales: p.exchangePrice,
        Cost: Math.round(p.exchangePrice * 0.6),
      })),
    [properties],
  );
  const revenueTrend = useMemo(
    () => {
      if (apiAnalytics) {
        return (apiAnalytics.revenueTrend || []).map((point) => ({
          period: point.period,
          Sales: point.revenue,
          Cost: point.cost,
        }));
      }
      return hasAnalyticsFallback ? fallbackRevenueTrend : [];
    },
    [apiAnalytics, fallbackRevenueTrend, hasAnalyticsFallback],
  );

  const fallbackFunnelData = useMemo(
    () => [
      { name: "Available", value: 120, fill: COLORS[0] },
      { name: "Reserved", value: 80, fill: COLORS[1] },
      { name: "Exchanged", value: 45, fill: COLORS[2] },
      { name: "Completed", value: 12, fill: COLORS[3] },
    ],
    [],
  );
  const funnelData = useMemo(
    () => {
      if (apiAnalytics) {
        return (apiAnalytics.conversionFunnel || []).map((item, index) => ({
          name: item.stage,
          value: item.value,
          fill: COLORS[index % COLORS.length],
        }));
      }
      return hasAnalyticsFallback ? fallbackFunnelData : [];
    },
    [apiAnalytics, fallbackFunnelData, hasAnalyticsFallback],
  );

  const fallbackTypeData = useMemo(() => {
    const counts = new Map<string, number>();
    properties.forEach((property) => {
      const key = property.bedrooms > 0 ? `${property.bedrooms} Bed` : "Studio";
      counts.set(key, (counts.get(key) || 0) + 1);
    });
    return Array.from(counts.entries()).map(([name, value]) => ({
      name,
      value,
    }));
  }, [properties]);
  const typeData = useMemo(
    () => {
      if (apiAnalytics) {
        return (apiAnalytics.assetAllocation || []).map((item) => ({
          name: item.type,
          value: item.value,
        }));
      }
      return hasAnalyticsFallback ? fallbackTypeData : [];
    },
    [apiAnalytics, fallbackTypeData, hasAnalyticsFallback],
  );

  const scatterData = useMemo(
    () => {
      if (apiAnalytics) {
        return (apiAnalytics.valuationMatrix || []).slice(0, 30).map((point) => ({
          x: point.sqft,
          y: point.price,
          z: point.psf,
        }));
      }

      if (hasAnalyticsFallback) {
        return properties.slice(0, 30).map((point) => ({
          x: point.sqft,
          y: point.exchangePrice,
          z: point.pricePSF,
        }));
      }

      return [];
    },
    [apiAnalytics, hasAnalyticsFallback, properties],
  );

  const fallbackRadialData = useMemo(
    () => [
      {
        name: "Sales",
        uv: Math.max(1, Math.round(properties.length * 0.67)),
        fill: COLORS[0],
      },
      {
        name: "Listing",
        uv: Math.max(1, Math.round(properties.length * 0.33)),
        fill: COLORS[1],
      },
    ],
    [properties],
  );
  const radialData = useMemo(
    () => {
      if (apiAnalytics) {
        return (apiAnalytics.kpiTargets || []).map((item, index) => ({
          name: item.category,
          uv: item.value,
          fill: COLORS[index % COLORS.length],
        }));
      }
      return hasAnalyticsFallback ? fallbackRadialData : [];
    },
    [apiAnalytics, fallbackRadialData, hasAnalyticsFallback],
  );

  const fallbackComposedData = useMemo(
    () =>
      properties.slice(0, 8).map((p, i) => ({
        period: `Q${i + 1}`,
        Volume: p.sqft * 10,
        "Deal Count": Math.floor(p.sqft / 500),
      })),
    [properties],
  );
  const composedData = useMemo(
    () => {
      if (apiAnalytics) {
        return (apiAnalytics.momentum || []).map((item) => ({
          period: item.period,
          Volume: item.volume,
          "Deal Count": item.deals,
        }));
      }
      return hasAnalyticsFallback ? fallbackComposedData : [];
    },
    [apiAnalytics, fallbackComposedData, hasAnalyticsFallback],
  );

  const fallbackRadarData = useMemo(
    () => [
      {
        s: "Avg Price",
        Score: fallbackKpis.totalRevenue / Math.max(properties.length, 1),
      },
      {
        s: "Avg Size",
        Score:
          properties.reduce((sum, p) => sum + p.sqft, 0) /
          Math.max(properties.length, 1),
      },
      { s: "Deal Count", Score: fallbackKpis.totalClients },
      { s: "Active Listings", Score: fallbackKpis.activePipeline },
    ],
    [fallbackKpis, properties],
  );
  const radarData = useMemo(
    () => {
      if (apiAnalytics) {
        return (apiAnalytics.portfolioScore || []).map((item) => ({
          s: item.metric,
          Score: item.score,
        }));
      }
      return hasAnalyticsFallback ? fallbackRadarData : [];
    },
    [apiAnalytics, fallbackRadarData, hasAnalyticsFallback],
  );

  const fallbackAgentData = useMemo(
    () => [
      { n: "Allsopp & Allsopp", v: 95 },
      { n: "Betterhomes", v: 88 },
      { n: "DDRE", v: 82 },
      { n: "Driven Properties", v: 75 },
      { n: "Emaar", v: 68 },
    ],
    [],
  );
  const agentData = useMemo(
    () => {
      if (apiAnalytics) {
        return (apiAnalytics.agentPerformance || []).map((item) => ({
          n: item.agent,
          v: item.score,
        }));
      }
      return hasAnalyticsFallback ? fallbackAgentData : [];
    },
    [apiAnalytics, fallbackAgentData, hasAnalyticsFallback],
  );

  const displayFunnelData = useMemo(
    () =>
      collapseSeries(funnelData, "value", "name", 6).map((item, index) => ({
        ...item,
        fill: COLORS[index % COLORS.length],
      })),
    [funnelData],
  );

  const displayTypeData = useMemo(
    () => collapseSeries(typeData, "value", "name", 6),
    [typeData],
  );

  const displayRadialData = useMemo(
    () =>
      collapseSeries(radialData, "uv", "name", 6).map((item, index) => ({
        ...item,
        fill: COLORS[index % COLORS.length],
      })),
    [radialData],
  );

  const displayAgentData = useMemo(
    () => collapseSeries(agentData, "v", "n", 8),
    [agentData],
  );

  const salesTrendMetric = useMemo(() => {
    if (revenueTrend.length < 2) return undefined;
    const first = revenueTrend[0].Sales || 0;
    const last = revenueTrend[revenueTrend.length - 1].Sales || 0;
    if (!first) return undefined;
    const change = ((last - first) / first) * 100;
    const prefix = change > 0 ? "+" : "";
    return `${prefix}${change.toFixed(1)}%`;
  }, [revenueTrend]);

  const isAnalyticsSectionLoading =
    isAnalyticsLoading && !hasAnalyticsFallback && !apiAnalytics;

  const chartTitleById: Record<string, string> = {
    revenue: "Sales Trend",
    funnel: "Deal Status Funnel",
    assets: "Bedroom Distribution",
    scatter: "Price vs. Size",
    radial: "Transaction Type Split",
    composed: "Quarterly Velocity",
    radar: "Portfolio Score",
    agent: "Top Agents by Volume",
  };

  const renderChartContent = (id: string) => {
    switch (id) {
      case "revenue":
        return (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={revenueTrend}>
              <defs>
                <linearGradient id="colRev" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#6366f1" stopOpacity={0.4} />
                  <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid
                strokeDasharray="3 3"
                opacity={0.1}
                vertical={false}
              />
              <XAxis
                dataKey="period"
                fontSize={10}
                axisLine={false}
                tickLine={false}
                stroke="#64748b"
                minTickGap={24}
                interval="preserveStartEnd"
                tickFormatter={(value) => truncateLabel(String(value), 8)}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area
                type="monotone"
                dataKey="Sales"
                name="Sales"
                stroke="#6366f1"
                strokeWidth={3}
                fill="url(#colRev)"
              />
              <Area
                type="monotone"
                dataKey="Cost"
                name="Cost"
                stroke="#ef4444"
                strokeWidth={2}
                strokeDasharray="5 5"
                fill="transparent"
              />
            </AreaChart>
          </ResponsiveContainer>
        );
      case "funnel":
        return (
          <ResponsiveContainer width="100%" height="100%">
            <FunnelChart margin={{ top: 10, right: 70, left: 10, bottom: 10 }}>
              <Tooltip content={<CustomTooltip />} />
              <Funnel dataKey="value" data={displayFunnelData} isAnimationActive>
                <LabelList
                  position="right"
                  fill="#94a3b8"
                  stroke="none"
                  dataKey="name"
                  fontSize={10}
                  formatter={(value) => truncateLabel(String(value ?? ""), 16)}
                />
              </Funnel>
            </FunnelChart>
          </ResponsiveContainer>
        );
      case "assets":
        return (
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Pie
                data={displayTypeData}
                innerRadius={60}
                outerRadius={80}
                paddingAngle={5}
                dataKey="value"
              >
                {displayTypeData.map((e, i) => (
                  <Cell
                    key={i}
                    fill={COLORS[i % COLORS.length]}
                    stroke="none"
                  />
                ))}
              </Pie>
              <Tooltip content={<CustomTooltip />} />
              <Legend
                verticalAlign="bottom"
                height={36}
                iconType="circle"
                formatter={(value) => truncateLabel(String(value ?? ""), 14)}
              />
            </PieChart>
          </ResponsiveContainer>
        );
      case "scatter":
        return (
          <ResponsiveContainer width="100%" height="100%">
            <ScatterChart margin={{ top: 20, right: 20, bottom: 20, left: 20 }}>
              <CartesianGrid strokeDasharray="3 3" opacity={0.1} />
              <XAxis
                type="number"
                dataKey="x"
                name="Size (SqFt)"
                unit="ft"
                stroke="#64748b"
                fontSize={10}
                tickLine={false}
                axisLine={false}
              />
              <YAxis
                type="number"
                dataKey="y"
                name="Price"
                stroke="#64748b"
                fontSize={10}
                tickLine={false}
                axisLine={false}
                tickFormatter={(v) => formatCurrency(v)}
              />
              <Tooltip
                cursor={{ strokeDasharray: "3 3" }}
                content={<CustomTooltip />}
              />
              <Scatter
                name="Price vs. Size"
                data={scatterData}
                fill="#10b981"
              />
            </ScatterChart>
          </ResponsiveContainer>
        );
      case "radial":
        return (
          <ResponsiveContainer width="100%" height="100%">
            <RadialBarChart
              cx="50%"
              cy="50%"
              innerRadius="30%"
              outerRadius="80%"
              barSize={15}
              data={displayRadialData}
            >
              <RadialBar background dataKey="uv" cornerRadius={10} />
              {/* Legend Moved to Top to Avoid Overlap */}
              <Legend
                iconSize={8}
                layout="horizontal"
                verticalAlign="top"
                align="center"
                wrapperStyle={{ fontSize: "10px", paddingBottom: "10px" }}
                formatter={(value) => truncateLabel(String(value ?? ""), 14)}
              />
              <Tooltip content={<CustomTooltip />} />
            </RadialBarChart>
          </ResponsiveContainer>
        );
      case "composed":
        return (
          <ResponsiveContainer width="100%" height="100%">
            <ComposedChart data={composedData}>
              <XAxis dataKey="period" hide />
              <Tooltip content={<CustomTooltip />} />
              <Bar
                dataKey="Volume"
                name="Sales Volume"
                barSize={20}
                fill="#6366f1"
                radius={[4, 4, 0, 0]}
              />
              <Line
                type="monotone"
                dataKey="Deal Count"
                name="Deal Count"
                stroke="#f59e0b"
                strokeWidth={3}
              />
            </ComposedChart>
          </ResponsiveContainer>
        );
      case "radar":
        return (
          <ResponsiveContainer width="100%" height="100%">
            <RadarChart cx="50%" cy="50%" outerRadius="70%" data={radarData}>
              <PolarGrid stroke="#334155" opacity={0.3} />
              <PolarAngleAxis
                dataKey="s"
                tick={{ fill: "#94a3b8", fontSize: 10 }}
                tickFormatter={(value) => truncateLabel(String(value), 12)}
              />
              <Radar
                name="Portfolio"
                dataKey="Score"
                stroke="#8b5cf6"
                fill="#8b5cf6"
                fillOpacity={0.4}
              />
              <Tooltip content={<CustomTooltip />} />
            </RadarChart>
          </ResponsiveContainer>
        );
      case "agent":
        return (
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={displayAgentData}
              layout="vertical"
              margin={{ left: 10, right: 10, top: 10, bottom: 10 }}
            >
              <XAxis type="number" hide />
              {/* Fixed: Increased YAxis width to prevent text clipping */}
              <YAxis
                dataKey="n"
                type="category"
                width={130}
                tick={{ fontSize: 10, fill: "#64748b" }}
                interval={0}
                tickFormatter={(value) => truncateLabel(String(value), 16)}
              />
              <Tooltip content={<CustomTooltip />} />
              <Bar
                dataKey="v"
                name="Sales Volume"
                fill="#ec4899"
                radius={[0, 4, 4, 0]}
                barSize={18}
                background={{ fill: "#f1f5f9" }}
              />
            </BarChart>
          </ResponsiveContainer>
        );
      default:
        return null;
    }
  };

  return (
    <div className="-m-4 lg:-m-6 pb-20 space-y-6">
      {/* Enlarged Chart Modal */}
      <AnimatePresence>
        {enlargedChart && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
            onClick={() => setEnlargedChart(null)}
          >
            <motion.div
              initial={{ scale: 0.9 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.9 }}
              className="bg-white dark:bg-slate-900 w-full max-w-5xl h-[80vh] rounded-2xl p-6 relative flex flex-col shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex justify-between items-center mb-4">
                <h2 className="text-2xl font-bold text-slate-800 dark:text-white capitalize">
                  {(enlargedChart && chartTitleById[enlargedChart]) ||
                    "Analysis"}
                </h2>
                <button
                  onClick={() => setEnlargedChart(null)}
                  className="p-2 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-full"
                >
                  <X size={24} />
                </button>
              </div>
              <div className="flex-1 w-full min-h-0">
                {renderChartContent(enlargedChart)}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* <MarketTicker /> */}

      <div className="p-4 lg:p-6 space-y-6">
        {isAnalyticsSectionLoading ? (
          <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 relative overflow-hidden min-h-[420px]">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(99,102,241,0.15),transparent_45%)]" />
            <div className="absolute inset-0 animate-pulse bg-gradient-to-r from-transparent via-slate-100/70 dark:via-slate-800/50 to-transparent" />
            <div className="relative z-10 h-full min-h-[420px] flex flex-col items-center justify-center text-center p-6">
              <div className="w-16 h-16 rounded-2xl bg-indigo-100 dark:bg-indigo-900/30 border border-indigo-200 dark:border-indigo-800 flex items-center justify-center mb-4 shadow-sm">
                <Loader2
                  size={28}
                  className="text-indigo-600 dark:text-indigo-300 animate-spin"
                />
              </div>
              <p className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                Building your market intelligence cockpit...
              </p>
              <p className="text-xs text-slate-500 mt-1">
                Loading summary cards and analytics visuals.
              </p>
            </div>
          </div>
        ) : (
          <>
            {/* KPI Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                {
                  label: "Total Sales Volume",
                  val: dashboardKpis.totalRevenue,
                  format: (value: number) => formatCurrency(value),
                  icon: Building2,
                  color: "text-emerald-400",
                  bg: "bg-emerald-500/10",
                  border: "border-emerald-500/20",
                },
                {
                  label: "Total Active Listings",
                  val: dashboardKpis.activePipeline,
                  format: (value: number) => value.toLocaleString(),
                  icon: Activity,
                  color: "text-green-400",
                  bg: "bg-green-500/10",
                  border: "border-green-500/20",
                },
                {
                  label: "Avg £ / SqFt",
                  val: dashboardKpis.avgYield,
                  format: (value: number) => formatPound(value, 2),
                  icon: Zap,
                  color: "text-amber-400",
                  bg: "bg-amber-500/10",
                  border: "border-amber-500/20",
                },
                {
                  label: "Total Transactions",
                  val: dashboardKpis.totalClients,
                  format: (value: number) => value.toLocaleString(),
                  icon: Users,
                  color: "text-rose-400",
                  bg: "bg-rose-500/10",
                  border: "border-rose-500/20",
                },
              ].map((k, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  className={cn(
                    "bg-white dark:bg-slate-900/80 backdrop-blur-xl border p-4 rounded-xl flex items-center justify-between shadow-lg hover:shadow-xl transition-all duration-300 group",
                    k.border,
                  )}
                >
                  <div>
                    <p className="text-xs font-bold text-slate-500 uppercase tracking-wider group-hover:text-slate-400 transition-colors">
                      {k.label}
                    </p>
                    <h3 className="text-2xl font-bold text-slate-900 dark:text-white mt-1">
                      {k.format(k.val)}
                    </h3>
                  </div>
                  <div
                    className={cn(
                      "p-3 rounded-xl shadow-[0_0_15px_rgba(0,0,0,0.1)] min-w-[56px] flex items-center justify-center",
                      k.bg,
                      k.color,
                    )}
                  >
                    <k.icon size={22} />
                  </div>
                </motion.div>
              ))}
            </div>

            {/* 10+ Graphs Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4 lg:gap-6 grid-flow-dense">
              <ChartCard
                title="Sales Trend"
                subtitle="Monthly Sales Volume"
                className="col-span-1 md:col-span-2 min-h-[320px]"
                metric={salesTrendMetric}
                onEnlarge={() => setEnlargedChart("revenue")}
              >
                {renderChartContent("revenue")}
              </ChartCard>

              <ChartCard
                title="Deal Status Funnel"
                subtitle="Available → Reserved → Exchanged → Completed"
                className="col-span-1 min-h-[320px]"
                onEnlarge={() => setEnlargedChart("funnel")}
              >
                {renderChartContent("funnel")}
              </ChartCard>

              <ChartCard
                title="Bedroom Distribution"
                subtitle="1 Bed, 2 Bed, Studio, etc."
                className="col-span-1 min-h-[320px]"
                onEnlarge={() => setEnlargedChart("assets")}
              >
                {renderChartContent("assets")}
              </ChartCard>

              <ChartCard
                title="Price vs. Size"
                subtitle="SqFt vs Price"
                className="col-span-1 md:col-span-2 min-h-[300px]"
                metric={`R² ${dashboardKpis.r2.toFixed(2)}`}
                onEnlarge={() => setEnlargedChart("scatter")}
              >
                {renderChartContent("scatter")}
              </ChartCard>

              <ChartCard
                title="Transaction Type Split"
                subtitle="Sale vs Listing"
                className="col-span-1 min-h-[300px]"
                onEnlarge={() => setEnlargedChart("radial")}
              >
                {renderChartContent("radial")}
              </ChartCard>

              <ChartCard
                title="Quarterly Velocity"
                subtitle="Volume & Deal Count per Quarter"
                className="col-span-1 min-h-[300px]"
                onEnlarge={() => setEnlargedChart("composed")}
              >
                {renderChartContent("composed")}
              </ChartCard>

              <ChartCard
                title="Portfolio Score"
                subtitle="Quality Check"
                className="col-span-1 min-h-[300px]"
                onEnlarge={() => setEnlargedChart("radar")}
              >
                {renderChartContent("radar")}
              </ChartCard>

              <ChartCard
                title="Top Agents by Volume"
                subtitle="Top 5"
                className="col-span-1 min-h-[300px]"
                onEnlarge={() => setEnlargedChart("agent")}
              >
                {renderChartContent("agent")}
              </ChartCard>
            </div>
          </>
        )}

        {hasAnalyticsFallback && (
          <div className="rounded-lg border border-amber-200 dark:border-amber-900/60 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-xs text-amber-800 dark:text-amber-200">
            Live analytics API is unavailable. Showing fallback chart data.
          </div>
        )}

        {/* Properties Accordion */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-sm overflow-hidden relative">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(99,102,241,0.08),transparent_40%)]" />
          <div className="relative p-4 border-b border-slate-200 dark:border-slate-800 flex flex-wrap gap-4 justify-between items-center bg-slate-50/50 dark:bg-slate-950/50 backdrop-blur">
            <div className="relative w-full sm:max-w-sm">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                size={16}
              />
              <input
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search community, project, developer, unit..."
                className="w-full pl-10 pr-4 py-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700 rounded-xl text-sm focus:ring-2 focus:ring-green-700 outline-none shadow-sm"
              />
            </div>
            <div className="flex items-center gap-2">
              {isSearchPending && (
                <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 dark:bg-slate-800 px-2 py-1 text-[10px] font-semibold text-slate-500">
                  <Loader2 size={12} className="animate-spin" />
                  Searching
                </span>
              )}
              <span className="text-xs font-mono text-slate-400 self-center font-bold">
                {isSearchSyncing ? "SEARCHING..." : `${filteredProjects.length} PROJECTS`}
              </span>
            </div>
          </div>

          <div className="relative p-3 sm:p-4 space-y-3 max-h-[680px] overflow-auto">
            {hasProjectFallback && !isProjectsLoading && (
              <div className="mb-3 rounded-lg border border-amber-200 dark:border-amber-900/60 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-xs text-amber-800 dark:text-amber-200">
                Live properties API is unavailable. Showing fallback demo data.
              </div>
            )}

            {isProjectsLoading ? (
              Array.from({ length: 5 }).map((_, idx) => (
                <div
                  key={`project-skeleton-${idx}`}
                  className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 p-4 animate-pulse"
                >
                  <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
                    <div className="md:col-span-2 space-y-2">
                      <div className="h-2 w-28 rounded bg-slate-200 dark:bg-slate-800" />
                      <div className="h-4 w-4/5 rounded bg-slate-200 dark:bg-slate-800" />
                      <div className="h-3 w-32 rounded bg-slate-200 dark:bg-slate-800" />
                    </div>
                    <div className="space-y-2">
                      <div className="h-2 w-20 rounded bg-slate-200 dark:bg-slate-800" />
                      <div className="h-4 w-28 rounded bg-slate-200 dark:bg-slate-800" />
                    </div>
                    <div className="space-y-2">
                      <div className="h-2 w-20 rounded bg-slate-200 dark:bg-slate-800" />
                      <div className="h-4 w-24 rounded bg-slate-200 dark:bg-slate-800" />
                    </div>
                    <div className="space-y-2">
                      <div className="h-2 w-24 rounded bg-slate-200 dark:bg-slate-800" />
                      <div className="h-4 w-20 rounded bg-slate-200 dark:bg-slate-800" />
                    </div>
                  </div>
                </div>
              ))
            ) : isSearchSyncing ? (
              <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950 py-10 px-4 text-center">
                <div className="inline-flex items-center gap-2 text-sm font-medium text-slate-600 dark:text-slate-300">
                  <Loader2 size={16} className="animate-spin" />
                  Finding matching properties...
                </div>
              </div>
            ) : filteredProjects.length === 0 ? (
              <div className="rounded-xl border border-dashed border-slate-300 dark:border-slate-700 py-10 text-center text-slate-500 text-sm">
                No project data available.
              </div>
            ) : (
              filteredProjects.map((project) => {
                const isExpanded = expandedProjectId === project.id;
                const units = project.units || [];
                const totalUnitsLabel = displayCount(project.totalUnits);
                return (
                  <motion.div
                    key={project.id}
                    initial={{ opacity: 0, y: 8 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="rounded-xl border border-slate-200 dark:border-slate-800 overflow-hidden bg-gradient-to-br from-white to-slate-50 dark:from-slate-950 dark:to-slate-900 shadow-sm"
                  >
                    <button
                      type="button"
                      onClick={() =>
                        setExpandedProjectId(isExpanded ? null : project.id)
                      }
                      className="w-full p-4 text-left hover:bg-slate-50/80 dark:hover:bg-slate-900/80 transition-colors"
                    >
                      <div className="flex items-start gap-3">
                        <div className="flex-1 grid grid-cols-1 md:grid-cols-5 gap-3">
                          <div className="md:col-span-2 min-w-0">
                            <p className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-indigo-500">
                              <Building2 size={11} />
                              Community / Property Name
                            </p>
                            <p className="mt-1 text-sm font-semibold text-slate-900 dark:text-white break-words">
                              {displayCommunityAndProperty(project)}
                            </p>
                            <p className="mt-1 text-[11px] text-slate-500 dark:text-slate-400 inline-flex items-center gap-1">
                              <Sparkles size={11} />
                              Total Units: {totalUnitsLabel}
                            </p>
                          </div>

                          <div>
                            <p className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-emerald-500">
                              <Zap size={11} />
                              Price Range
                            </p>
                            <p className="mt-1 text-sm font-semibold text-slate-900 dark:text-white">
                              {displayPriceRange(project.priceRange)}
                            </p>
                          </div>

                          <div>
                            <p className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-sky-500">
                              <Activity size={11} />
                              Developer
                            </p>
                            <p className="mt-1 text-sm font-semibold text-slate-900 dark:text-white">
                              {displayText(project.developer)}
                            </p>
                          </div>

                          <div>
                            <p className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-amber-500">
                              <Users size={11} />
                              Units Sold / Available
                            </p>
                            <p className="mt-1 text-sm font-semibold text-slate-900 dark:text-white">
                              {displayCount(project.unitsSold)} /{" "}
                              {displayCount(project.unitsAvailable)}
                            </p>
                            <p className="text-[11px] text-slate-500 dark:text-slate-400">
                              {project.soldPercentage !== null &&
                              project.soldPercentage !== undefined
                                ? `${project.soldPercentage.toFixed(1)}% sold`
                                : "N/A"}
                            </p>
                          </div>
                        </div>

                        <div
                          className={cn(
                            "mt-1 shrink-0 text-slate-500 transition-transform",
                            isExpanded ? "rotate-180" : "rotate-0",
                          )}
                        >
                          <ChevronDown size={16} />
                        </div>
                      </div>
                    </button>

                    <AnimatePresence initial={false}>
                      {isExpanded && (
                        <motion.div
                          initial={{ height: 0, opacity: 0 }}
                          animate={{ height: "auto", opacity: 1 }}
                          exit={{ height: 0, opacity: 0 }}
                          transition={{ duration: 0.2 }}
                          className="border-t border-slate-200 dark:border-slate-800"
                        >
                          <div className="p-4 overflow-x-auto">
                            <table className="w-full min-w-[640px] text-sm">
                              <thead className="bg-slate-100/80 dark:bg-slate-900/70">
                                <tr>
                                  <th className="p-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500">
                                    BuildingName
                                  </th>
                                  <th className="p-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500">
                                    Unitnumber
                                  </th>
                                  <th className="p-3 text-left text-[10px] font-bold uppercase tracking-wider text-slate-500">
                                    UnitType
                                  </th>
                                  <th className="p-3 text-right text-[10px] font-bold uppercase tracking-wider text-slate-500">
                                    Area
                                  </th>
                                  <th className="p-3 text-right text-[10px] font-bold uppercase tracking-wider text-slate-500">
                                    Price
                                  </th>
                                </tr>
                              </thead>
                              <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                                {units.length > 0 ? (
                                  units.map((unit, index) => (
                                    <tr
                                      key={`${project.id}-${index}`}
                                      className="hover:bg-indigo-50/40 dark:hover:bg-indigo-900/10 transition-colors"
                                    >
                                      <td className="p-3 text-slate-700 dark:text-slate-200">
                                        {displayText(unit.buildingName)}
                                      </td>
                                      <td className="p-3 text-slate-700 dark:text-slate-200">
                                        {displayText(unit.unitNumber)}
                                      </td>
                                      <td className="p-3 text-slate-700 dark:text-slate-200">
                                        {displayText(unit.unitType)}
                                      </td>
                                      <td className="p-3 text-right font-mono text-slate-700 dark:text-slate-200">
                                        {displayArea(unit.area)}
                                      </td>
                                      <td className="p-3 text-right font-mono text-slate-900 dark:text-white font-semibold">
                                        {displayCurrency(unit.price)}
                                      </td>
                                    </tr>
                                  ))
                                ) : (
                                  <tr>
                                    <td
                                      colSpan={5}
                                      className="p-4 text-center text-slate-500 text-sm"
                                    >
                                      No unit data available.
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </motion.div>
                );
              })
            )}
          </div>
        </div>
      </div>

      {/* <div className="mt-10">
        <h2 className="text-lg font-bold mb-4 text-slate-900 dark:text-white">
          Uploaded Files
        </h2>
        <div className="bg-white dark:bg-slate-900 rounded-2xl border shadow-sm p-6">
          {uploadedFiles.length === 0 ? (
            <p className="text-slate-500">No files uploaded yet.</p>
          ) : (
            <ul className="space-y-4">
              {uploadedFiles.map((file) => (
                <li key={file.name} className="flex items-center gap-4">
                  <span className="text-sm text-slate-700 dark:text-slate-200">{file.name}</span>
                  <button
                    className="bg-indigo-600 text-white px-3 py-1 rounded-lg font-bold shadow hover:bg-indigo-700"
                    onClick={() => handleDownload(file.url)}
                  >
                    Download
                  </button>
                  <button
                    className="bg-rose-600 text-white px-3 py-1 rounded-lg font-bold shadow hover:bg-rose-700"
                    onClick={() => handleDelete(file.name)}
                  >
                    Delete
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className="mt-10">
        <h2 className="text-lg font-bold mb-4 text-slate-900 dark:text-white">
          Recent Activity
        </h2>
        <div className="bg-white dark:bg-slate-900 rounded-2xl border shadow-sm p-6">
          <ul className="space-y-4">
            <li className="flex items-center gap-4">
              <Activity className="text-emerald-400" size={20} />
              <span className="text-sm text-slate-700 dark:text-slate-200">
                Uploaded new property data for Dubai Marina.
              </span>
              <span className="ml-auto text-xs text-slate-400">2h ago</span>
            </li>
            <li className="flex items-center gap-4">
              <Activity className="text-indigo-400" size={20} />
              <span className="text-sm text-slate-700 dark:text-slate-200">
                Compared properties in Downtown.
              </span>
              <span className="ml-auto text-xs text-slate-400">4h ago</span>
            </li>
            <li className="flex items-center gap-4">
              <Activity className="text-rose-400" size={20} />
              <span className="text-sm text-slate-700 dark:text-slate-200">
                Downloaded valuation report for Palm Jumeirah.
              </span>
              <span className="ml-auto text-xs text-slate-400">1d ago</span>
            </li>
          </ul>
        </div>
      </div> */}
    </div>
  );
}
